class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.bound) {
      overlay.dataset.bound = 'true';
      overlay.addEventListener('click', this.close.bind(this));
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasOpen = this.classList.contains('active');

    // The open animation (Stack + Decode) is handled in mxdvs-cart-drawer.css by the .active
    // class. A timeout makes sure the transition always runs, even right after the DOM is replaced.
    setTimeout(() => {
      // Check the page colour behind the drawer before it shows (extra dark layer on light pages)
      if (window.MxCart && window.MxCart.lightCheck) window.MxCart.lightCheck(this);
      this.classList.add('animate', 'active');
      if (window.MxCart) {
        // Titles and amounts decode only when the drawer really opens, not on a re-render while open
        if (!wasOpen && window.MxCart.decodeOpen) window.MxCart.decodeOpen(this, false);
        // Phones: drag down or slide left to close (the panel is new after every full re-render)
        if (window.MxCart.sheet) window.MxCart.sheet(this);
        // Phones: videos behind the cart pause while it is open (11.10.2026)
        if (!wasOpen && window.MxCart.pauseVideos) window.MxCart.pauseVideos(true);
      }
      document.dispatchEvent(new CustomEvent('mx:cart-open'));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.mx-head__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // The text scrambles away while the rows drop out
    if (this.classList.contains('active') && window.MxCart && window.MxCart.decodeOpen) window.MxCart.decodeOpen(this, true);
    this.classList.remove('active');
    // Videos paused by the open cart play again
    if (window.MxCart && window.MxCart.pauseVideos) window.MxCart.pauseVideos(false);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    // First add fix (10.10.2026): a first add into an empty, closed drawer must not play the
    // empty-to-full rebuild slide (right / width / min-width), or the drawer slides in from the
    // centre. The rebuild inside an already open drawer (MxCart.rebuildFromEmpty) stays.
    if (this.classList.contains('is-empty') && !this.classList.contains('active')) {
      this.classList.add('mx-no-rebuild');
      setTimeout(() => this.classList.remove('mx-no-rebuild'), 1500);
    }
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const hasItems = !this.querySelector('.drawer__inner-empty');
    this.classList.toggle('is-empty', !hasItems);

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// cart.js can load after this file. If CartItems is not ready yet, cart.js defines cart-drawer-items itself.
if (typeof CartItems !== 'undefined' && !customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ------------------------------------------------------------------ */
/* MXDVS extras: shipping bar, freebies, recommendations, note, size  */
/* swap, light-page layer, digit and letter effects.                  */
/* ------------------------------------------------------------------ */

(function () {
  if (window.MxCart) return;
  var M = (window.MxCart = { _lastFlapPos: -1, _linePrices: {} });

  M.cfg = function () {
    var node = document.getElementById('MxCartConfig');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };

  M.rate = function () {
    var r = window.Shopify && window.Shopify.currency ? parseFloat(window.Shopify.currency.rate) : 1;
    return r > 0 ? r : 1;
  };

  M.esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  M.money = function (value, currency, withDecimals) {
    var whole = Math.abs(value - Math.round(value)) < 0.005;
    var digits = withDecimals && !whole ? 2 : 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EUR',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(value);
    } catch (e) {
      return (currency || 'EUR') + ' ' + value.toFixed(digits);
    }
  };

  M.fill = function (template, values) {
    return M.esc(template)
      .replace(/\[amount\]/g, values.amount || '')
      .replace(/\[percent\]/g, values.percent || '')
      .replace(/\[threshold\]/g, values.threshold || '')
      .replace(/\[country\]/g, values.country || '');
  };

  M.reduced = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------- Light page behind the cart: extra black layer ---------- */
  // Reads the background colour of the page at a few points behind the drawer (the right
  // side for the full cart, the center for the empty cart). Mostly bright means the layer
  // switches on. Videos count as dark; images are skipped and the colour behind them is used.
  M.parseColor = function (value) {
    var m = String(value || '').match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    if (p.length < 3) return null;
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
  };

  M.pointLight = function (drawer, x, y) {
    var list = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (drawer.contains(el)) continue;
      if (el.tagName === 'VIDEO' || el.tagName === 'IFRAME') return false;
      var c = M.parseColor(getComputedStyle(el).backgroundColor);
      if (c && c.a > 0.5) return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255 > 0.7;
    }
    // Nothing painted a background: the browser default behind the page is white
    return true;
  };

  M.lightCheck = function (drawer) {
    var box = drawer && drawer.querySelector('#CartDrawer');
    if (!box) return;
    if (box.dataset.lightCheck !== 'true') {
      box.classList.remove('is-light-page');
      return;
    }
    var w = window.innerWidth;
    var h = window.innerHeight;
    var xs = drawer.classList.contains('is-empty') || w < 990 ? [0.3, 0.5, 0.7] : [0.72, 0.84, 0.96];
    var ys = [0.2, 0.5, 0.8];
    var light = 0;
    var total = 0;
    xs.forEach(function (x) {
      ys.forEach(function (y) {
        total++;
        if (M.pointLight(drawer, x * w, y * h)) light++;
      });
    });
    box.classList.toggle('is-light-page', light / total >= 0.5);
  };

  M.ship = function (cfg) {
    var tiers = cfg && cfg.tiers;
    if (!tiers || !tiers.zones) return null;
    var country = cfg.country;
    if ((tiers.noship || []).indexOf(country) > -1) return { noship: true };

    var zone = null;
    for (var i = 0; i < tiers.zones.length; i++) {
      if (tiers.zones[i].c.indexOf(country) > -1) {
        zone = tiers.zones[i];
        break;
      }
    }
    if (!zone || !zone.free) return null;

    var rate = M.rate();
    var sub = (cfg.subtotal || 0) / 100;
    var fT = Math.ceil(zone.free * rate);
    var dT = zone.disc ? Math.ceil(zone.disc * rate) : 0;
    var useD = !!cfg.showDisc && dT > 0 && dT < fT;

    return {
      sub: sub,
      fT: fT,
      dT: dT,
      useD: useD,
      pct: tiers.disc_pct || 30,
      inFree: sub >= fT,
      inDisc: useD && sub >= dT && sub < fT,
      fee: zone.fee ? zone.fee * rate : 0,
      cur: cfg.currency,
    };
  };

  M.liveFee = function (cfg) {
    var key = 'mxfee:' + cfg.country + ':' + cfg.subtotal + ':' + cfg.count;
    try {
      var hit = sessionStorage.getItem(key);
      if (hit !== null) return Promise.resolve(parseFloat(hit) || null);
    } catch (e) {}
    if (M._feeReq && M._feeReq.key === key) return M._feeReq.promise;

    var base = (window.routes && window.routes.cart_url) || '/cart';
    var query = 'shipping_address%5Bcountry%5D=' + encodeURIComponent(cfg.country);

    function wait(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }

    function poll(attempt) {
      return fetch(base + '/async_shipping_rates.json?' + query, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          if (data && data.shipping_rates) {
            var paid = data.shipping_rates
              .map(function (r) {
                return parseFloat(r.price);
              })
              .filter(function (v) {
                return v > 0;
              });
            var fee = paid.length ? Math.min.apply(null, paid) : null;
            try {
              sessionStorage.setItem(key, fee ? String(fee) : '0');
            } catch (e) {}
            return fee;
          }
          if (attempt < 5) {
            return wait(500).then(function () {
              return poll(attempt + 1);
            });
          }
          return null;
        });
    }

    var promise = fetch(base + '/prepare_shipping_rates.json?' + query, { method: 'POST' })
      .then(function () {
        return poll(0);
      })
      .catch(function () {
        return null;
      });

    M._feeReq = { key: key, promise: promise };
    return promise;
  };

  /* ---------- Terminal typewriter: matches sections/news-banner.liquid exactly ---------- */
  // Types the text one character at a time with the same randomized per-letter speed as
  // the news banner, shows a blinking block cursor after, and calls onDone once typing
  // finishes (used to trigger the shine on "FREE SHIPPING UNLOCKED").
  M.typewrite = function (el, text, onDone, speedMs) {
    if (M._typers && M._typers.has(el)) clearTimeout(M._typers.get(el));
    M._typers = M._typers || new Map();

    var speed = speedMs || 42;
    el.innerHTML = '<span class="mx-type-text"></span><span class="mx-type-cursor" aria-hidden="true"></span>';
    var target = el.querySelector('.mx-type-text');

    if (M.reduced() || !text) {
      target.textContent = text;
      if (onDone) onDone();
      return;
    }

    el.classList.add('is-typing');
    var count = 0;
    (function type() {
      count++;
      target.textContent = text.slice(0, count);
      if (count < text.length) {
        var t = setTimeout(type, speed + Math.random() * speed * 0.6);
        M._typers.set(el, t);
      } else {
        el.classList.remove('is-typing');
        if (onDone) onDone();
      }
    })();
  };

  /* ---------- Remember the last cart total, so changes can be animated ---------- */
  // Stored per browser tab, so the bar also slides after adding from a product page.
  M.lastSub = function () {
    try {
      var v = sessionStorage.getItem('mxLastSub');
      return v === null ? null : parseFloat(v);
    } catch (e) {
      return null;
    }
  };
  M.saveSub = function (v) {
    try {
      sessionStorage.setItem('mxLastSub', String(v));
    } catch (e) {}
  };

  // Decode: every letter scrambles through random characters, then all settle together
  var DECODE_POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#%&*';
  // keep (optional): characters that never scramble, e.g. /[\s>]/
  M.decode = function (el, text, stepMs, steps, keep) {
    if (!el) return;
    clearInterval(el._mxDecode);
    if (M.reduced()) {
      el.textContent = text;
      return;
    }
    var t = 0;
    var total = steps || 6;
    el._mxDecode = setInterval(function () {
      t++;
      el.textContent = text
        .split('')
        .map(function (ch) {
          return t >= total || ch === ' ' || (keep && keep.test(ch)) ? ch : DECODE_POOL[Math.floor(Math.random() * DECODE_POOL.length)];
        })
        .join('');
      if (t >= total) clearInterval(el._mxDecode);
    }, stepMs || 40);
  };

  // Decode for amounts: only the digits scramble, the currency sign, dots and commas stay put.
  // durationMs sets how long the scramble runs (default 240ms), then every digit settles at once.
  M.decodeNum = function (el, text, durationMs) {
    if (!el) return;
    clearInterval(el._mxDecode);
    if (M.reduced()) {
      el.textContent = text;
      return;
    }
    var steps = Math.max(1, Math.round((durationMs || 240) / 40));
    var t = 0;
    el._mxDecode = setInterval(function () {
      t++;
      el.textContent = text
        .split('')
        .map(function (ch) {
          return t >= steps || !/[0-9]/.test(ch) ? ch : String(Math.floor(Math.random() * 10));
        })
        .join('');
      if (t >= steps) clearInterval(el._mxDecode);
    }, 40);
  };

  // Decodes a price block: a plain amount, or an old (struck) price plus the new (red) one.
  // Both start decoding together and stay visible; the red amount keeps scrambling extraMs
  // longer, so the struck price settles first.
  M.decodeAmounts = function (el, extraMs) {
    if (!el) return;
    var oldEl = el.querySelector('.mx-old');
    var saleEl = el.querySelector('.mx-sale');
    if (!oldEl && !saleEl) {
      M.decodeNum(el, el.textContent.trim());
      return;
    }
    if (oldEl) M.decodeNum(oldEl, oldEl.textContent.trim(), 240);
    if (saleEl) M.decodeNum(saleEl, saleEl.textContent.trim(), 240 + (oldEl ? extraMs || 0 : 0));
  };

  // Types a value out like "FREE SHIPPING UNLOCKED": terminal letters, no cursor left behind
  M.typeOnce = function (el, text) {
    if (!el) return;
    M.typewrite(el, text, function () {
      var c = el.querySelector('.mx-type-cursor');
      if (c) c.remove();
    });
  };

  /* Line price: remembers the last price per cart line and decodes when it changes
     (quantity + or -). New lines and swapped sizes just show their price. */
  class MxLinePrice extends HTMLElement {
    connectedCallback() {
      var key = this.dataset.key;
      if (!key) return;
      var text = this.textContent.replace(/\s+/g, ' ').trim();
      var prev = M._linePrices[key];
      M._linePrices[key] = text;
      if (prev !== undefined && prev !== text) M.decodeAmounts(this, 500);
    }
  }

  /* Shipping progress bar: the dot is the cart's current position and slides live */
  class MxShipBar extends HTMLElement {
    connectedCallback() {
      // After every re-render of the drawer: undo lines back in place, a product that just came
      // in opens and fades in, recommendation tiles slide or fade. On the first page load that
      // code further down is not set up yet, so this waits for it. Runs once the whole drawer is
      // in place (microtask), before the screen is painted.
      Promise.resolve().then(function () {
        if (typeof M.afterRender === 'function') {
          try {
            M.afterRender();
          } catch (e) {}
        }
      });
      var cfg = M.cfg();
      if (!cfg || !cfg.showShip) return;
      var s = M.ship(cfg);
      if (!s) return;

      var W = cfg.words || {};

      if (s.noship) {
        this.innerHTML =
          '<div class="mx-ship__inner"><p class="mx-noship">' +
          M.fill(W.noship, { country: M.esc(cfg.countryName || cfg.country) }) +
          '</p></div>';
        this.hidden = false;
        return;
      }

      var cur = s.cur;
      var money = function (v) {
        return M.money(v, cur, false);
      };
      var vals = function (amount, threshold) {
        return {
          amount: amount != null ? money(amount) : '',
          percent: String(s.pct),
          threshold: threshold != null ? money(threshold) : '',
        };
      };

      var pctOf = function (v) {
        return Math.max(0, Math.min(100, (v / s.fT) * 100));
      };
      var pct = pctOf(s.sub);
      var prevSub = M.lastSub();
      var changed = prevSub !== null && Math.abs(prevSub - s.sub) > 0.005;
      var fromPct = changed ? pctOf(prevSub) : pct;
      var dPos = pctOf(s.dT);

      var tipD =
        M.fill(W.tipD, vals(null, s.dT)) + '<br>' + (s.sub >= s.dT ? 'Unlocked' : money(s.dT - s.sub) + ' to go');
      var tipF = M.fill(W.tipF, vals(null, s.fT)) + '<br>' + (s.inFree ? 'Unlocked' : money(s.fT - s.sub) + ' to go');
      var tipNow = money(s.sub) + ' in cart' + (s.inFree ? '' : '<br>' + money(s.fT - s.sub) + ' to free shipping');

      // Milestones are small plain markers; only the "you are here" dot carries the beam
      var marker = function (pos, reached, isEnd, label, tip) {
        return (
          '<button type="button" class="mx-mark' +
          (reached ? ' is-hit' : '') +
          (isEnd ? ' mx-mark--end' : '') +
          '" style="left:' +
          pos +
          '%" aria-label="' +
          M.esc(label) +
          '"><span class="mx-tip" role="tooltip">' +
          tip +
          '</span></button>'
        );
      };

      // Next freebie: the first tier the cart has not reached yet (thresholds in EUR, converted).
      // [freebie] becomes the underlined name; hover (mouse) or tap (touch) shows its photo underneath.
      var nextFree = '';
      if (cfg.freebiesOn) {
        var rate = M.rate();
        var tiers = (cfg.freebies || [])
          .filter(function (f) {
            return f.title;
          })
          .map(function (f) {
            return { title: f.title, img: f.img, need: Math.ceil((f.t || 0) * rate) };
          })
          .sort(function (a, b) {
            return a.need - b.need;
          });
        var nextTier = null;
        for (var ti = 0; ti < tiers.length; ti++) {
          if (s.sub < tiers[ti].need) {
            nextTier = tiers[ti];
            break;
          }
        }
        if (tiers.length) {
          if (nextTier) {
            var fbBtn =
              '<button type="button" class="mx-fb" aria-expanded="false">' +
              M.esc(nextTier.title) +
              '<span class="mx-fb__pop" role="tooltip">' +
              (nextTier.img
                ? '<span class="mx-fb__img" style="background-image:url(\'' + M.esc(nextTier.img) + '\')"></span>'
                : '') +
              '<span class="mx-fb__name">' +
              M.esc(nextTier.title) +
              '</span><span class="mx-fb__when">From ' +
              M.esc(money(nextTier.need)) +
              '</span></span></button>';
            nextFree = M.fill(W.nextFreebie || '[amount] to next freebie: [freebie]', {
              amount: money(nextTier.need - s.sub),
            }).replace(/\[freebie\]/g, fbBtn);
          } else {
            nextFree = M.esc(W.allFreebies || 'All freebies included');
          }
        }
      }

      // Right side above the bar: distance while below the threshold, "reached" once free
      var msg;
      if (s.inFree) msg = W.freeReached || 'Free shipping reached';
      else if (s.inDisc) msg = M.fill(W.disc, vals(null, s.dT));
      else if (s.useD) msg = M.fill(W.below, vals(s.dT - s.sub, s.dT));
      else msg = M.fill(W.goal, vals(s.fT - s.sub, s.fT));
      msg = msg.replace(/<[^>]+>/g, '');

      this.innerHTML =
        '<div class="mx-ship__inner">' +
        '<div class="mx-ship__meta"><span class="mx-ship__total" id="MxShipTotal">' +
        M.esc(money(s.sub) + ' in cart') +
        '</span><span class="mx-ship__msg" id="MxShipMsg"></span></div>' +
        '<div class="mx-bar">' +
        '<div class="mx-bar__fill keep-empty' +
        (s.inFree ? ' is-full' : '') +
        '" style="width:' +
        fromPct +
        '%"></div>' +
        (s.useD ? marker(dPos, s.sub >= s.dT, false, s.pct + '% off shipping from ' + money(s.dT), tipD) : '') +
        marker(100, s.inFree, true, 'Free shipping from ' + money(s.fT), tipF) +
        '<button type="button" class="mx-dot mx-dot--now" style="left:' +
        fromPct +
        '%" aria-label="' +
        M.esc(money(s.sub) + ' in cart') +
        '"><span class="mx-beam" aria-hidden="true"><i></i></span><span class="mx-tip" role="tooltip">' +
        tipNow +
        '</span></button>' +
        '</div>' +
        '<div class="mx-ship__labels"><span>' +
        (s.useD ? money(s.dT) + ' &middot; ' + s.pct + '% off' : money(0)) +
        (nextFree ? ' &middot; <b class="mx-ship__next">' + nextFree + '</b>' : '') +
        '</span><span class="mx-ship__end">' +
        (s.inFree ? M.esc(M.fill(W.reached, vals(null, s.fT)).replace(/<[^>]+>/g, '')) : money(s.fT) + ' &middot; Free') +
        '</span></div>' +
        '</div>';
      this.hidden = false;

      // Slide fill and dot from the previous total to the new one
      if (changed) {
        var fill = this.querySelector('.mx-bar__fill');
        var now = this.querySelector('.mx-dot--now');
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            fill.style.width = pct + '%';
            now.style.left = pct + '%';
          });
        });
        M.typeOnce(this.querySelector('#MxShipTotal'), money(s.sub) + ' in cart');
      }

      // Typed with the terminal cursor after it, also once free shipping is reached
      M.typewrite(this.querySelector('#MxShipMsg'), msg);

      M.bindFreebiePreview(this);
      // Tooltips on the dot and the ticks shift sideways so they stay inside the drawer (11.10.2026)
      M.bindTips(this);
    }
  }

  /* Next freebie photo: hover with a mouse, tap on touch. A click pins it open; a tap or
     click anywhere else, a second tap on the name, or Escape closes it. */
  M.fbOpen = function (fb, open) {
    if (!fb) return;
    fb.classList.toggle('is-open', open);
    fb.setAttribute('aria-expanded', open);
    if (!open) {
      delete fb.dataset.pinned;
      return;
    }
    // Keep the photo inside the drawer: shift it sideways if it would stick out
    var pop = fb.querySelector('.mx-fb__pop');
    var panel = fb.closest('.mx-drawer__inner');
    if (!pop || !panel) return;
    // Footer chips sit in a row that scrolls sideways: the photo hangs from the footer
    // instead (see mxdvs-cart-items.css), placed above the chip that opened it
    var row = fb.closest('.mx-fchips');
    var foot = fb.closest('.mx-foot');
    if (row && foot) {
      var c = fb.getBoundingClientRect();
      var f = foot.getBoundingClientRect();
      pop.style.setProperty('--fb-x', c.left + c.width / 2 - f.left + 'px');
      pop.style.setProperty('--fb-y', c.top - f.top - 8.5 + 'px');
    }
    pop.style.setProperty('--fb-shift', '0px');
    var r = pop.getBoundingClientRect();
    var box = panel.getBoundingClientRect();
    var edge = 17.5;
    var shift = 0;
    if (r.left < box.left + edge) shift = box.left + edge - r.left;
    else if (r.right > box.right - edge) shift = box.right - edge - r.right;
    pop.style.setProperty('--fb-shift', shift + 'px');
  };

  /* Shipping bar tooltips stay inside the drawer (11.10.2026). On hover, focus or tap, the
     tooltip is measured and shifted sideways (--tip-shift, mxdvs-cart-items.css) until it is
     17.5px from the drawer's edge, so it never leaves the screen at either end of the bar. */
  M.tipFit = function (btn) {
    var tip = btn && btn.querySelector('.mx-tip');
    var panel = btn && btn.closest('.mx-drawer__inner');
    if (!tip || !panel) return;
    tip.style.setProperty('--tip-shift', '0px');
    var edge = 17.5;
    var r = tip.getBoundingClientRect();
    var box = panel.getBoundingClientRect();
    var shift = 0;
    if (r.right > box.right - edge) shift = box.right - edge - r.right;
    if (r.left + shift < box.left + edge) shift = box.left + edge - r.left;
    tip.style.setProperty('--tip-shift', shift + 'px');
  };

  M.bindTips = function (host) {
    if (host.dataset.tipBound) return;
    host.dataset.tipBound = 'true';
    var run = function (e) {
      var b = e.target && e.target.closest ? e.target.closest('.mx-dot, .mx-mark') : null;
      if (b) M.tipFit(b);
    };
    host.addEventListener('pointerover', run);
    host.addEventListener('focusin', run);
    host.addEventListener('touchstart', run, { passive: true });
  };

  M.bindFreebiePreview = function (host) {
    if (host.dataset.fbBound) return;
    host.dataset.fbBound = 'true';
    host.addEventListener('pointerover', function (e) {
      if (e.pointerType !== 'mouse') return;
      var fb = e.target.closest('.mx-fb');
      if (fb) M.fbOpen(fb, true);
    });
    host.addEventListener('pointerout', function (e) {
      if (e.pointerType !== 'mouse') return;
      var fb = e.target.closest('.mx-fb');
      if (fb && !fb.contains(e.relatedTarget) && !fb.dataset.pinned) M.fbOpen(fb, false);
    });
    host.addEventListener('click', function (e) {
      var fb = e.target.closest('.mx-fb');
      if (!fb) return;
      e.preventDefault();
      if (fb.dataset.pinned) {
        M.fbOpen(fb, false);
      } else {
        M.fbOpen(fb, true);
        fb.dataset.pinned = 'true';
      }
    });
    if (!M._fbDocBound) {
      M._fbDocBound = true;
      document.addEventListener('pointerdown', function (e) {
        var open = document.querySelector('#CartDrawer .mx-fb.is-open');
        if (open && !open.contains(e.target)) M.fbOpen(open, false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = document.querySelector('#CartDrawer .mx-fb.is-open');
        if (open) M.fbOpen(open, false);
      });
      // Scrolling the footer chip row sideways closes an open photo (scroll does not bubble)
      document.addEventListener(
        'scroll',
        function (e) {
          if (!e.target || !e.target.classList || !e.target.classList.contains('mx-fchips')) return;
          var open = document.querySelector('#CartDrawer .mx-fchips .mx-fb.is-open');
          if (open) M.fbOpen(open, false);
        },
        true
      );
    }
  };

  /* Shipping row in the footer: old fee struck through next to the new one */
  class MxShipFoot extends HTMLElement {
    connectedCallback() {
      var cfg = M.cfg();
      if (!cfg) return;

      // Subtotal: decodes to the new amount, only when it changed. Runs after the bar has
      // read the previous total, then stores the new one. With a discount, both amounts
      // decode together and the red total settles 0.5s after the struck price.
      var subNow = (cfg.subtotal || 0) / 100;
      var subPrev = M.lastSub();
      var foot = this.closest('.mx-foot');
      if (foot && subPrev !== null && Math.abs(subPrev - subNow) > 0.005) {
        var val = foot.querySelector('.mx-foot__totals .mx-foot__row:first-child .mx-foot__val');
        if (val) M.decodeAmounts(val, 500);
      }
      M.saveSub(subNow);

      var s = M.ship(cfg);
      if (!s || s.noship) return;
      // Always visible: the live fee for the visitor's zone, struck through once free
      this.draw(s, s.fee);
      var self = this;
      M.liveFee(cfg).then(function (fee) {
        if (fee != null && self.isConnected) self.draw(s, fee);
      });
    }
    draw(s, fee) {
      var old = fee > 0 && (s.inFree || s.inDisc) ? '<s class="mx-old">' + M.money(fee, s.cur, true) + '</s>' : '';
      var now = s.inFree ? 'Free' : s.inDisc ? M.money(fee * (1 - s.pct / 100), s.cur, true) : M.money(fee, s.cur, true);
      if (!fee && !s.inFree) now = s.inDisc ? s.pct + '% off' : 'Free';
      var cfgAll = M.cfg() || {};
      var cfgW = cfgAll.words || {};
      // [country] in the shipping time line becomes the visitor's country (11.10.2026)
      var stText = cfgW.shipTime ? cfgW.shipTime.replace(/\[country\]/g, cfgAll.countryName || '') : '';
      var st = stText ? '<span class="mx-ship-time">' + M.esc(stText) + '</span>' : '';
      this.innerHTML = '<span class="mx-foot__label"><span>Shipping</span>' + st + '</span><span>' + old + '<strong>' + now + '</strong></span>';
      this.hidden = false;
    }
  }

  /* Freebies */
  M.syncFreebies = function (cfg, unlocked) {
    var names = unlocked
      .map(function (f) {
        return f.title;
      })
      .join(', ');
    var images = unlocked
      .map(function (f) {
        return f.img;
      })
      .filter(Boolean)
      .join(' | ');
    var current = cfg.attrs || {};
    // Hanger and tote (from mxdvs.packaging, counted in sections/cart-drawer.liquid) travel
    // as the attribute "Included", for the order confirmation email. Never a cart line.
    var included = cfg.included || '';
    if ((current.freebies || '') === names && (current.images || '') === images && (current.included || '') === included) {
      M._sync = Promise.resolve();
      return M._sync;
    }
    M._sync = fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Freebies: names, _freebie_images: images, Included: included } }),
    }).catch(function () {});
    return M._sync;
  };

  class MxFreebies extends HTMLElement {
    connectedCallback() {
      var cfg = M.cfg();
      if (!cfg || !cfg.freebiesOn) return;

      var rate = M.rate();
      var sub = (cfg.subtotal || 0) / 100;
      var list = (cfg.freebies || [])
        .filter(function (f) {
          return f.title;
        })
        .map(function (f) {
          return { title: f.title, img: f.img, need: Math.ceil((f.t || 0) * rate) };
        })
        .sort(function (a, b) {
          return a.need - b.need;
        });

      var unlocked =
        cfg.count > 0
          ? list.filter(function (f) {
              return sub >= f.need;
            })
          : [];
      var next = null;
      for (var i = 0; i < list.length; i++) {
        if (sub < list[i].need) {
          next = list[i];
          break;
        }
      }

      M.syncFreebies(cfg, unlocked);

      var media = function (f) {
        return f.img
          ? '<span class="mx-item__media"><img src="' + M.esc(f.img) + '" alt="" loading="lazy" width="200" height="200"></span>'
          : '<span class="mx-item__media mx-item__media--empty"></span>';
      };

      var html = '';

      // The next freebie to unlock comes first, above the ones already included
      if (next && cfg.showLocked) {
        html +=
          '<div class="mx-item mx-item--locked">' +
          media(next) +
          '<div class="mx-item__info"><p class="mx-item__title">' +
          M.esc(next.title) +
          '</p><p class="mx-item__opt">Next freebie</p><p class="mx-item__unlock">Spend ' +
          M.money(next.need - sub, cfg.currency, false) +
          ' more to unlock</p></div></div>';
      }

      html += unlocked
        .map(function (f) {
          return (
            '<div class="mx-item mx-item--gift">' +
            media(f) +
            '<div class="mx-item__info"><p class="mx-item__title">' +
            M.esc(f.title) +
            '</p><p class="mx-item__opt">Free from ' +
            M.money(f.need, cfg.currency, false) +
            '</p><p class="mx-item__opt"><span class="mx-tag">Included</span></p></div></div>'
          );
        })
        .join('');

      // Footer version: included freebies as small chips, the photo shows on hover or tap
      if (this.dataset.style === 'chips') {
        var title = (cfg.words && cfg.words.freebiesTitle) || '';
        var chips = unlocked
          .map(function (f) {
            return (
              '<span class="mx-fchip"><button type="button" class="mx-fb" aria-expanded="false">' +
              M.esc(f.title) +
              '<span class="mx-fb__pop" role="tooltip">' +
              (f.img ? '<span class="mx-fb__img" style="background-image:url(\'' + M.esc(f.img) + '\')"></span>' : '') +
              '<span class="mx-fb__name">' +
              M.esc(f.title) +
              '</span><span class="mx-fb__when">From ' +
              M.esc(M.money(f.need, cfg.currency, false)) +
              '</span></span></button></span>'
            );
          })
          .join('');
        this.innerHTML = chips
          ? (title ? '<p class="mx-freebies__title">' + M.esc(title) + '</p>' : '') + '<div class="mx-fchips">' + chips + '</div>'
          : '';
        this.hidden = !chips;
        M.bindFreebiePreview(this);
        return;
      }

      // "Freebies:" heading on top, same style as the other section titles
      var heading = (cfg.words && cfg.words.freebiesTitle) || '';
      if (html && heading) html = '<h3 class="mx-recs__title mx-freebies__title">' + M.esc(heading) + '</h3>' + html;

      this.innerHTML = html;
      this.hidden = !html;
    }
  }

  /* Recommendations: picked groups */
  class MxUpsell extends HTMLElement {
    connectedCallback() {
      var groups = Array.prototype.slice.call(this.querySelectorAll('.mx-recs__group')).filter(function (g) {
        return g.querySelector('.mx-rec');
      });
      if (!groups.length) {
        this.hidden = true;
        return;
      }
      // When "Still thinking about it" mode has real recently-viewed products, hide this fallback
      if (this.dataset.fallback === 'true' && M._recentFilled) {
        this.hidden = true;
        return;
      }

      var saved = null;
      try {
        saved = sessionStorage.getItem('mxUpsellGroup');
      } catch (e) {}
      var chosen =
        groups.filter(function (g) {
          return g.dataset.group === saved;
        })[0] || groups[Math.floor(Math.random() * groups.length)];
      try {
        sessionStorage.setItem('mxUpsellGroup', chosen.dataset.group);
      } catch (e) {}

      chosen.hidden = false;
      this.hidden = false;

      if (!M._groupShown) {
        M._groupShown = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'cart_upsell_group_shown', upsell_group: chosen.dataset.group });
      }

      if (!this.dataset.bound) {
        this.dataset.bound = 'true';
        this.addEventListener('click', this.onAdd.bind(this));
      }
    }

    onAdd(event) {
      M.recClick(event);
    }
  }

  /* ---------- Recommendations: add without leaving the cart (approved 30.09.2026) ----------
     + on a product without options adds it straight away. + on a product with sizes or other
     options slides a picker up over the photo; choosing the last missing option adds that
     variant. Either way the drawer re-renders in place (cart.js listens for cartUpdate), so the
     new line builds in and the product leaves the recommendations. No reload. */
  M.recData = function (btn) {
    var node = btn.parentNode && btn.parentNode.querySelector('.mx-rec__data');
    if (!node) return null;
    try {
      var d = JSON.parse(node.textContent);
      d.options = (d.options || []).map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });
      return d.options.length && (d.variants || []).length ? d : null;
    } catch (e) {
      return null;
    }
  };

  M.recChosen = function (pick) {
    var out = {};
    pick.querySelectorAll('.mx-pick__chip.is-picked').forEach(function (c) {
      out[+c.dataset.o] = c.dataset.v;
    });
    return out;
  };

  // A value is struck through when no in-stock variant has it together with what is already picked
  M.recRefresh = function (pick) {
    var data = pick._data;
    var chosen = M.recChosen(pick);
    pick.querySelectorAll('.mx-pick__chip').forEach(function (c) {
      var oi = +c.dataset.o;
      var v = c.dataset.v;
      var ok = data.variants.some(function (x) {
        if (!x.a || x.o[oi] !== v) return false;
        for (var k = 0; k < data.options.length; k++) {
          if (k !== oi && chosen[k] != null && x.o[k] !== chosen[k]) return false;
        }
        return true;
      });
      c.disabled = !ok;
      if (!ok) c.classList.remove('is-picked');
    });
  };

  M.recBuild = function (btn, data) {
    var media = btn.closest('.mx-rec__media');
    var pick = document.createElement('div');
    pick.className = 'mx-pick';
    pick.setAttribute('role', 'group');
    pick.setAttribute('aria-label', btn.getAttribute('aria-label') || '');
    pick.innerHTML =
      data.options
        .map(function (name, oi) {
          var values = [];
          data.variants.forEach(function (v) {
            if (values.indexOf(v.o[oi]) === -1) values.push(v.o[oi]);
          });
          return (
            '<div class="mx-pick__opt"><p class="mx-pick__name">' +
            M.esc(name) +
            '</p><div class="mx-pick__chips">' +
            values
              .map(function (x) {
                return (
                  '<button type="button" class="mx-pick__chip" data-o="' + oi + '" data-v="' + M.esc(x) + '">' + M.esc(x) + '</button>'
                );
              })
              .join('') +
            '</div></div>'
          );
        })
        .join('') +
      '<div class="mx-pick__line" aria-hidden="true"><i></i></div><div class="mx-pick__status" role="status"></div>';
    pick._data = data;
    media.appendChild(pick);
    M.recRefresh(pick);
    return pick;
  };

  M.recOpen = function (rec, open) {
    document.querySelectorAll('#CartDrawer .mx-rec.is-open').forEach(function (o) {
      if (o === rec || o.querySelector('.mx-pick.is-loading')) return;
      o.classList.remove('is-open');
      var b = o.querySelector('.mx-rec__add');
      if (b) b.setAttribute('aria-expanded', 'false');
    });
    if (!rec) return;
    rec.classList.toggle('is-open', open);
    var btn = rec.querySelector('.mx-rec__add');
    if (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-label', (open ? 'Close options for ' : 'Choose options for ') + (btn.dataset.title || ''));
    }
    if (open) {
      var first = rec.querySelector('.mx-pick__chip:not(:disabled)');
      if (first) {
        setTimeout(function () {
          first.focus({ preventScroll: true });
        }, 300);
      }
    }
  };

  M.recAdd = function (btn, variantId, pick) {
    var group = btn.dataset.upsellGroup;
    btn.disabled = true;
    btn.classList.add('is-loading');
    if (pick) {
      pick.classList.add('is-loading');
      M.typewrite(pick.querySelector('.mx-pick__status'), '> writing to cart');
    }
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _upsell_group: group, _source: 'cart_drawer_upsell' },
      }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) throw new Error(data.description || data.message || '');
          return data;
        });
      })
      .then(function (data) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'cart_upsell_add_to_cart', upsell_group: group, product_id: variantId });
        // The tile fades out, the product opens its space in the cart and fades in,
        // the other tiles slide into place and the next one fades in (12.10.2026)
        M.arriveNext(variantId);
        M.recSnapshot();
        var tile = btn.closest('.mx-rec');
        if (tile && !M.reduced()) tile.classList.add('is-leaving');
        setTimeout(
          function () {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: data });
            }
          },
          tile && !M.reduced() ? 260 : 0
        );
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        if (pick) {
          pick.classList.remove('is-loading');
          var st = pick.querySelector('.mx-pick__status');
          if (st) st.textContent = '> ' + ((err && err.message) || 'could not add, try again');
        }
      });
  };

  M.recClick = function (event) {
    var btn = event.target.closest('.mx-rec__add');
    if (btn && btn.tagName === 'BUTTON') {
      event.preventDefault();
      if (btn.disabled) return;
      var rec = btn.closest('.mx-rec');
      if (btn.dataset.picker !== 'true') {
        M.recAdd(btn, btn.dataset.variantId, null);
        return;
      }
      if (!btn.dataset.title) {
        var t = rec.querySelector('.mx-rec__title');
        btn.dataset.title = t ? t.textContent.trim() : '';
      }
      var pick = rec.querySelector('.mx-pick');
      if (pick && pick.classList.contains('is-loading')) return;
      if (!pick) {
        var data = M.recData(btn);
        if (!data) {
          if (btn.dataset.url) window.location.href = btn.dataset.url;
          return;
        }
        pick = M.recBuild(btn, data);
        void pick.offsetWidth;
      }
      M.recOpen(rec, !rec.classList.contains('is-open'));
      return;
    }

    var chip = event.target.closest('.mx-pick__chip');
    if (!chip || chip.disabled) return;
    var box = chip.closest('.mx-pick');
    if (box.classList.contains('is-loading')) return;
    box.querySelectorAll('.mx-pick__chip[data-o="' + chip.dataset.o + '"]').forEach(function (c) {
      c.classList.remove('is-picked');
    });
    chip.classList.add('is-picked');
    M.decode(chip, chip.dataset.v, 40, 6);
    M.recRefresh(box);
    var status = box.querySelector('.mx-pick__status');
    if (status) status.textContent = '';

    var chosen = M.recChosen(box);
    var opts = box._data.options;
    if (Object.keys(chosen).length < opts.length) {
      // Still an option to pick: its chips light up twice
      box.classList.remove('is-nudge');
      void box.offsetWidth;
      box.classList.add('is-nudge');
      return;
    }
    var match = box._data.variants.filter(function (v) {
      return (
        v.a &&
        v.o.every(function (x, i) {
          return x === chosen[i];
        })
      );
    })[0];
    if (!match) {
      if (status) status.textContent = '> not in stock';
      return;
    }
    M.recAdd(chip.closest('.mx-rec').querySelector('.mx-rec__add'), match.id, box);
  };

  // Close with a click outside or Escape; decode the chips on hover and on tap
  if (!M._recDocBound) {
    M._recDocBound = true;
    document.addEventListener('click', function (e) {
      if (e.target.closest('#CartDrawer .mx-pick, #CartDrawer .mx-rec__add')) return;
      M.recOpen(null, false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('#CartDrawer .mx-rec.is-open');
      if (!open || open.querySelector('.mx-pick.is-loading')) return;
      M.recOpen(open, false);
      var b = open.querySelector('.mx-rec__add');
      if (b) b.focus();
    });
    var chipDecode = function (e) {
      var chip = e.target.closest && e.target.closest('#CartDrawer .mx-pick__chip');
      if (!chip || chip.disabled || chip.classList.contains('is-picked') || chip._mxHover) return;
      if (chip.closest('.mx-pick.is-loading')) return;
      chip._mxHover = true;
      M.decode(chip, chip.dataset.v, 40, 6);
      setTimeout(function () {
        chip._mxHover = false;
      }, 300);
    };
    document.addEventListener('mouseover', chipDecode);
    document.addEventListener('touchstart', chipDecode, { passive: true });
  }

  /* Recommendations: "Still thinking about it?", recently viewed, from this browser only.
     Product pages should call MxCart.trackView('<handle>') once on load to feed this. */
  M.trackView = function (handle) {
    if (!handle) return;
    var key = 'mxRecentlyViewed';
    var list = [];
    try {
      list = JSON.parse(localStorage.getItem(key) || '[]');
    } catch (e) {}
    list = list.filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    list = list.slice(0, 8);
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {}
  };

  class MxRecent extends HTMLElement {
    connectedCallback() {
      var cfg = M.cfg();
      if (!cfg) return;
      var key = 'mxRecentlyViewed';
      var list = [];
      try {
        list = JSON.parse(localStorage.getItem(key) || '[]');
      } catch (e) {}
      var inCart = cfg.handles || [];
      list = list.filter(function (h) {
        return inCart.indexOf(h) === -1;
      });
      if (!list.length) {
        this.hidden = true;
        return;
      }
      list = list.slice(0, 3);

      var self = this;
      Promise.all(
        list.map(function (handle) {
          return fetch('/products/' + handle + '.js')
            .then(function (r) {
              return r.ok ? r.json() : null;
            })
            .catch(function () {
              return null;
            });
        })
      ).then(function (products) {
        products = products.filter(function (p) {
          return p && p.available;
        });
        if (!products.length) {
          self.hidden = true;
          return;
        }
        M._recentFilled = true;
        var grid = self.querySelector('.mx-recs__grid');
        grid.innerHTML = products
          .map(function (p) {
            var img = p.featured_image ? p.featured_image.replace(/\.(\w+)(\?.*)?$/, '_300x.$1$2') : '';
            var variant = p.variants[0];
            var pickData = JSON.stringify({
              options: p.options,
              variants: p.variants.map(function (v) {
                return { id: v.id, o: v.options, a: v.available };
              }),
            }).replace(/</g, '\\u003c');
            var addBtn =
              p.variants.length === 1
                ? '<button type="button" class="mx-rec__add" data-variant-id="' +
                  variant.id +
                  '" data-upsell-group="recent" aria-label="Add ' +
                  M.esc(p.title) +
                  ' to cart">+</button>'
                : '<button type="button" class="mx-rec__add" data-picker="true" data-upsell-group="recent" data-url="' +
                  M.esc(p.url) +
                  '" aria-expanded="false" aria-label="Choose options for ' +
                  M.esc(p.title) +
                  '">+</button><script type="application/json" class="mx-rec__data">' +
                  pickData +
                  '</' +
                  'script>';
            return (
              '<div class="mx-rec"><div class="mx-rec__media"><a href="' +
              p.url +
              '" class="mx-rec__link" tabindex="-1" aria-hidden="true">' +
              (img ? '<img class="mx-rec__img" src="' + img + '" alt="" loading="lazy" width="150" height="150">' : '') +
              '</a>' +
              addBtn +
              '</div><a href="' +
              p.url +
              '" class="mx-rec__title">' +
              M.esc(p.title) +
              '</a><p class="mx-rec__price">' +
              M.money(p.price / 100, cfg.currency, false) +
              '</p></div>'
            );
          })
          .join('');
        self.hidden = false;
        if (!self.dataset.bound) {
          self.dataset.bound = 'true';
          self.addEventListener('click', MxUpsell.prototype.onAdd.bind(self));
        }
        // Hide the fallback picked-products block, if it already rendered first
        var fallback = document.querySelector('mx-upsell[data-fallback="true"]');
        if (fallback) fallback.hidden = true;
      });
    }
  }

  /* Cart note: slow slide open, random placeholder, terminal underline while typing */
  class MxNote extends HTMLElement {
    connectedCallback() {
      var cfg = M.cfg() || {};
      this.lines = String(cfg.noteLines || '')
        .split('\n')
        .map(function (l) {
          return l.trim();
        })
        .filter(Boolean);
      this.btn = this.querySelector('.mx-note__tgl');
      this.panel = this.querySelector('.mx-note__panel');
      this.area = this.querySelector('textarea');
      this.count = this.querySelector('.mx-term__count');
      this.underline = this.querySelector('.mx-term__line i');
      if (!this.btn || !this.panel || !this.area) return;

      this.pickPlaceholder();

      // Closed by default; opens with a slide when the + is pressed

      this.btn.addEventListener('click', () => {
        var open = !this.panel.classList.contains('is-open');
        if (open) this.pickPlaceholder();
        this.set(open, true);
      });

      this.area.addEventListener('input', () => {
        if (this.count) this.count.textContent = this.area.value.length + '/500';
      });

      this.initTerminal();
    }

    /* Same idea as the search page input: the real text is hidden and redrawn with a
       blinking underscore caret right after the last typed character. */
    initTerminal() {
      var area = this.area;
      if (area.dataset.terminal) return;
      area.dataset.terminal = 'true';

      var field = document.createElement('div');
      field.className = 'mx-term__field';
      area.parentNode.insertBefore(field, area);
      field.appendChild(area);

      var mirror = document.createElement('div');
      mirror.className = 'mx-term__mirror';
      mirror.setAttribute('aria-hidden', 'true');
      field.appendChild(mirror);

      var lastLength = area.value.length;

      var grow = function () {
        area.style.height = 'auto';
        area.style.height = area.scrollHeight + 'px';
      };

      var render = function (typed) {
        var value = area.value;
        var pos = typeof area.selectionStart === 'number' ? area.selectionStart : value.length;
        mirror.textContent = '';

        if (!value) {
          var caretEmpty = document.createElement('span');
          caretEmpty.className = 'mx-term__caret';
          mirror.appendChild(caretEmpty);
          var ghost = document.createElement('span');
          ghost.className = 'mx-term__ghost';
          ghost.textContent = area.getAttribute('placeholder') || '';
          mirror.appendChild(ghost);
        } else {
          var before = value.slice(0, pos);
          var after = value.slice(pos);
          if (typed && before.length) {
            mirror.appendChild(document.createTextNode(before.slice(0, -1)));
            var fresh = document.createElement('span');
            fresh.className = 'mx-term__new';
            fresh.textContent = before.slice(-1);
            mirror.appendChild(fresh);
          } else {
            mirror.appendChild(document.createTextNode(before));
          }
          var caret = document.createElement('span');
          caret.className = 'mx-term__caret';
          mirror.appendChild(caret);
          mirror.appendChild(document.createTextNode(after));
        }
        grow();
      };

      area.addEventListener('input', function () {
        var typed = area.value.length > lastLength;
        lastLength = area.value.length;
        render(typed);
      });
      ['keyup', 'click', 'select'].forEach(function (evt) {
        area.addEventListener(evt, function () {
          render(false);
        });
      });
      area.addEventListener('focus', function () {
        field.classList.add('is-focus');
        render(false);
      });
      area.addEventListener('blur', function () {
        field.classList.remove('is-focus');
      });

      // Redraw when a new placeholder is picked on open
      new MutationObserver(function () {
        render(false);
      }).observe(area, { attributes: true, attributeFilter: ['placeholder'] });

      render(false);
    }

    pickPlaceholder() {
      if (!this.lines.length) return;
      var n = Math.floor(Math.random() * this.lines.length);
      if (this.lines.length > 1 && n === M._lastNote) n = (n + 1) % this.lines.length;
      M._lastNote = n;
      this.area.setAttribute('placeholder', this.lines[n]);
    }

    set(open, animate) {
      if (!animate) this.panel.classList.add('is-instant');
      this.panel.classList.toggle('is-open', open);
      if (open) this.panel.removeAttribute('inert');
      else this.panel.setAttribute('inert', '');
      this.btn.textContent = open ? '−' : '+';
      this.btn.setAttribute('aria-expanded', open);
      this.btn.setAttribute('aria-label', open ? 'Close note' : 'Open note');
      try {
        sessionStorage.setItem('mxNoteOpen', open ? '1' : '0');
      } catch (e) {}
      if (!animate) {
        var panel = this.panel;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            panel.classList.remove('is-instant');
          });
        });
      }
      if (open && animate) {
        var area = this.area;
        setTimeout(function () {
          area.focus({ preventScroll: true });
        }, 350);
      }
    }
  }

  // The visible name inside a size button: the full name on desktop, the short one on phones
  M.sizeText = function (opt) {
    var parts = opt.querySelectorAll('.mx-size__full, .mx-size__short');
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].getClientRects().length) return parts[i];
    }
    return opt;
  };

  /* Size swap: replaces the line's variant without leaving the drawer */
  class MxSizeSwap extends HTMLElement {
    connectedCallback() {
      // Just swapped: the new size decodes once and settles with a short flash
      if (M._swapped) {
        var done = this.querySelector('.mx-size__opt.is-current[data-variant-id="' + M._swapped + '"]');
        if (done) {
          M._swapped = null;
          var doneEl = M.sizeText(done);
          var doneLabel = doneEl.textContent.trim();
          done.classList.add('is-done');
          M.decode(doneEl, doneLabel, 40, 6);
          setTimeout(function () {
            done.classList.remove('is-done');
          }, 500);
        }
      }

      this.addEventListener('click', (event) => {
        var opt = event.target.closest('.mx-size__opt');
        if (!opt || opt.disabled || opt.classList.contains('is-current')) return;
        this.swap(opt.dataset.variantId);
      });

      // Hover: the size decodes (scrambles, then settles). Each button holds the full name
      // (desktop) and the short name (phones); only the visible one decodes.
      this.querySelectorAll('.mx-size__opt:not(.is-current):not(:disabled)').forEach(function (opt) {
        var run = function () {
          var el = M.sizeText(opt);
          if (el._mxLabel === undefined) el._mxLabel = el.textContent.trim();
          M.decode(el, el._mxLabel, 40, 6);
        };
        opt.addEventListener('mouseenter', run);
        opt.addEventListener('focus', run);
        opt.addEventListener('touchstart', run, { passive: true });
        opt.addEventListener('mouseleave', function () {
          var el = M.sizeText(opt);
          clearInterval(el._mxDecode);
          if (el._mxLabel !== undefined) el.textContent = el._mxLabel;
        });
      });
    }

    // Add the new size, read the cart back to confirm it is there, and only then remove
    // the old size. If anything fails, the old size stays and a short message shows.
    swap(newVariantId) {
      if (this.dataset.busy === 'true') return;
      this.dataset.busy = 'true';
      this.classList.add('is-loading');

      // Loading (variation C): the picked size goes black, a 2px line sweeps under the
      // sizes and the status types out. The drawer re-renders when the cart answers.
      var pick = this.querySelector('.mx-size__opt[data-variant-id="' + newVariantId + '"]');
      if (pick) {
        pick.classList.add('is-pending');
        pick.setAttribute('aria-busy', 'true');
      }
      var line = document.createElement('div');
      line.className = 'mx-size__line';
      line.setAttribute('aria-hidden', 'true');
      line.innerHTML = '<i></i>';
      var status = document.createElement('span');
      status.className = 'mx-size__status';
      status.setAttribute('role', 'status');
      this.append(line, status);
      // Types out, then decodes once: letters scramble, then settle (12.10.2026)
      M.typewrite(status, '> writing to cart', function () {
        M.decode(status.querySelector('.mx-type-text'), '> writing to cart', 40, 6, /[\s>]/);
      });
      M._swapped = String(newVariantId);

      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var oldKey = this.dataset.key;
      var qty = parseInt(this.dataset.quantity, 10) || 1;
      // Line properties must be sent as key/value pairs. Liquid writes "no properties"
      // as an empty list [], which Shopify rejects ("expected Array to be a Hash").
      var props = {};
      try {
        var raw = JSON.parse(this.dataset.properties || '{}');
        if (Array.isArray(raw)) {
          raw.forEach(function (pair) {
            if (Array.isArray(pair) && pair.length === 2) props[pair[0]] = pair[1];
          });
        } else if (raw && typeof raw === 'object') {
          props = raw;
        }
      } catch (e) {}
      var json = { 'Content-Type': 'application/json', Accept: 'application/json' };

      function fail(message) {
        self.dataset.busy = 'false';
        self.classList.remove('is-loading');
        M._swapped = null;
        if (pick) {
          pick.classList.remove('is-pending');
          pick.removeAttribute('aria-busy');
        }
        line.remove();
        status.remove();
        var item = self.closest('.mx-item');
        var out = item && item.querySelector('.cart-item__error-text');
        if (out) out.textContent = message || 'That size could not be selected. Please try again.';
      }

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: json,
        body: JSON.stringify({
          items: [Object.keys(props).length ? { id: Number(newVariantId), quantity: qty, properties: props } : { id: Number(newVariantId), quantity: qty }],
        }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) throw new Error(data.description || data.message || '');
            return data;
          });
        })
        .then(function () {
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          var hasNew = cart.items.some(function (i) {
            return String(i.variant_id) === String(newVariantId);
          });
          if (!hasNew) throw new Error('');
          var hasOld = cart.items.some(function (i) {
            return i.key === oldKey;
          });
          if (!hasOld) return cart;
          return fetch(root + 'cart/change.js', {
            method: 'POST',
            headers: json,
            body: JSON.stringify({ id: oldKey, quantity: 0 }),
          }).then(function (r) {
            return r.json();
          });
        })
        .then(function () {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mx-size-swap', cartData: {} });
          }
        })
        .catch(function (err) {
          fail(err && err.message);
        });
    }
  }

  /* Empty cart: status terminal under "continue shopping" and a random typed line */
  class MxEmpty extends HTMLElement {
    connectedCallback() {
      this.onOpen = this.run.bind(this);
      document.addEventListener('mx:cart-open', this.onOpen);
      this.reserveStatus();
      var drawer = this.closest('cart-drawer');
      if (drawer && drawer.classList.contains('active')) this.run();
    }

    disconnectedCallback() {
      document.removeEventListener('mx:cart-open', this.onOpen);
    }

    // All seven rows exist from the first moment (empty but full height), so typing
    // never pushes "Your cart is empty" down
    reserveStatus() {
      var status = this.querySelector('[data-mx-status]');
      if (!status) return [];
      status.innerHTML = '';
      var rows = [];
      for (var r = 0; r < 7; r++) {
        var row = document.createElement('div');
        row.className = 'mx-status__row';
        row.innerHTML = '&nbsp;';
        status.appendChild(row);
        rows.push(row);
      }
      return rows;
    }

    run() {
      var cfg = M.cfg() || {};
      M.saveSub(0);
      var cur = cfg.currency || 'EUR';
      var zero;
      try {
        zero = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: cur,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
        }).format(0);
      } catch (e) {
        zero = '0.00';
      }

      // Random line, typed out, no cursor left behind
      var lines = String(this.dataset.lines || '')
        .split('\n')
        .map(function (l) {
          return l.trim();
        })
        .filter(Boolean);
      var textEl = this.querySelector('[data-mx-empty-text]');
      if (textEl && lines.length) {
        var n = Math.floor(Math.random() * lines.length);
        if (lines.length > 1 && n === M._lastEmpty) n = (n + 1) % lines.length;
        M._lastEmpty = n;
        M.typeOnce(textEl, lines[n]);
      }

      // Status terminal, typed line by line into the reserved rows; only the last keeps its cursor
      var rowEls = this.reserveStatus();
      if (!rowEls.length) return;
      var rows = [
        'CART_STATUS',
        '──────────────',
        'ITEMS:       0',
        'SUBTOTAL:    ' + zero,
        'REGRETS:     0',
        '',
        '> awaiting input',
      ];
      var token = (this._statusRun = (this._statusRun || 0) + 1);
      var self = this;
      var i = 0;
      (function next() {
        if (i >= rows.length || token !== self._statusRun) return;
        var row = rowEls[i];
        var text = rows[i];
        var last = i === rows.length - 1;
        i++;
        if (!text) {
          next();
          return;
        }
        M.typewrite(
          row,
          text,
          function () {
            if (!last) {
              var c = row.querySelector('.mx-type-cursor');
              if (c) c.remove();
            }
            next();
          },
          14
        );
      })();
    }
  }

  customElements.define('mx-ship-bar', MxShipBar);
  customElements.define('mx-ship-foot', MxShipFoot);
  customElements.define('mx-line-price', MxLinePrice);
  customElements.define('mx-freebies', MxFreebies);
  customElements.define('mx-upsell', MxUpsell);
  customElements.define('mx-recent', MxRecent);
  customElements.define('mx-note', MxNote);

  /* From empty to full without a reload: the empty view fades, the panel slides from the
     center to the right edge (CSS transition on right/width), then the cart builds in. */
  M.rebuildFromEmpty = function () {
    var drawer = document.querySelector('cart-drawer');
    var inner = drawer && drawer.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;
    var base = (window.routes && window.routes.cart_url) || '/cart';
    inner.classList.add('mx-fading');
    var sections = fetch(base + '?sections=cart-drawer,cart-icon-bubble').then(function (r) {
      return r.json();
    });
    setTimeout(function () {
      sections
        .then(function (sec) {
          var html = new DOMParser().parseFromString(sec['cart-drawer'], 'text/html');
          var src = html.querySelector('#CartDrawer .drawer__inner');
          if (src) {
            drawer.classList.remove('is-empty');
            inner.classList.remove('mx-fading');
            inner.classList.add('mx-rebuilt');
            inner.innerHTML = src.innerHTML;
            M.lightCheck(drawer);
            setTimeout(function () {
              inner.classList.remove('mx-rebuilt');
            }, 2400);
          }
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sec['cart-icon-bubble']) {
            var b = new DOMParser().parseFromString(sec['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
            if (b) bubble.innerHTML = b.innerHTML;
          }
        })
        .catch(function () {
          window.location.reload();
        });
    }, 350);
  };

  /* Empty cart: random product slot machine */
  M.slotPool = function (skipTags, count) {
    if (M._slotPool) return M._slotPool;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var skip = String(skipTags || '')
      .toLowerCase()
      .split(',')
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
    var perPage = 250;
    var pages = Math.max(1, Math.ceil((parseInt(count, 10) || 0) / perPage));
    var page = 1 + Math.floor(Math.random() * pages);

    function usable(p) {
      if (!p || !p.handle) return false;
      if (String(p.product_type || '').toLowerCase().indexOf('gift') !== -1) return false;
      var inStock = (p.variants || []).some(function (v) {
        return v.available;
      });
      if (!inStock) return false;
      var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
      return !tags.some(function (t) {
        return skip.indexOf(String(t).trim().toLowerCase()) !== -1;
      });
    }
    function get(pg) {
      return fetch(root + 'products.json?limit=' + perPage + '&page=' + pg, { credentials: 'same-origin' })
        .then(function (r) {
          return r.ok ? r.json() : { products: [] };
        })
        .then(function (d) {
          return (d.products || []).filter(usable);
        });
    }
    M._slotPool = get(page)
      .then(function (list) {
        return list.length || page === 1 ? list : get(1);
      })
      .catch(function () {
        return [];
      });
    return M._slotPool;
  };

  class MxSlot extends HTMLElement {
    connectedCallback() {
      this.reel = this.querySelector('[data-slot-reel]');
      this.status = this.querySelector('[data-slot-status]');
      this.countEl = this.querySelector('[data-slot-count]');
      this.btn = this.querySelector('[data-slot-spin]');
      this.current = null;
      this.spins = 0;
      if (!this.reel) return;

      this.btn.addEventListener('click', () => this.spin());
      // The +: add to the cart, then rebuild the drawer from empty to full (no reload)
      this.addEventListener('click', (event) => {
        var button = event.target.closest('button.mx-rec__add');
        if (!button || button.disabled) return;
        this.add(button);
      });

      this.onOpen = () => {
        if (this.dataset.auto === 'true') this.spin();
        else if (!this.current) this.spin(true);
      };
      document.addEventListener('mx:cart-open', this.onOpen);
      var drawer = this.closest('cart-drawer');
      if (drawer && drawer.classList.contains('active')) this.onOpen();
    }

    disconnectedCallback() {
      document.removeEventListener('mx:cart-open', this.onOpen);
    }

    add(button) {
      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      button.disabled = true;
      this.btn.disabled = true;
      this.status.innerHTML = '&gt; adding';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _source: 'cart_drawer_slot' } }],
        }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) throw new Error(data.description || '');
            return data;
          });
        })
        .then(function () {
          self.status.innerHTML = '&gt; added to cart';
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'cart_upsell_add_to_cart', upsell_group: 'slot', product_id: button.dataset.variantId });
          setTimeout(M.rebuildFromEmpty, 500);
        })
        .catch(function () {
          self.status.innerHTML = '&gt; could not add, try again';
          button.disabled = false;
          self.btn.disabled = false;
        });
    }

    image(p) {
      var src = p.images && p.images[0] ? p.images[0].src || p.images[0] : '';
      if (!src) return '';
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=300';
    }

    card(p, final) {
      var img = this.image(p);
      var media =
        '<div class="mx-rec__media">' +
        (img ? '<span class="mx-slot__img" style="background-image:url(\'' + M.esc(img) + '\')"></span>' : '') +
        (final ? final.add : '') +
        '</div>';
      return (
        '<div class="mx-slot__card">' +
        (final ? '<a href="' + M.esc(final.url) + '" class="mx-slot__link" tabindex="-1" aria-hidden="true"></a>' : '') +
        media +
        '<div class="mx-slot__info"><p class="mx-rec__title">' +
        M.esc(p.title) +
        '</p><p class="mx-rec__price">' +
        (final ? final.price : '') +
        '</p></div></div>'
      );
    }

    // quiet = land on a product without the spin (used when "spin on open" is off)
    spin(quiet) {
      if (this.busy) return;
      this.busy = true;
      this.btn.disabled = true;
      this.status.innerHTML = '&gt; loading';
      var self = this;
      var reduce = M.reduced();
      var cfg = M.cfg() || {};

      M.slotPool(this.dataset.skip, this.dataset.count).then(function (pool) {
        if (!pool.length) {
          self.status.innerHTML = '&gt; nothing in stock';
          self.busy = false;
          self.btn.disabled = false;
          return;
        }
        var target;
        do {
          target = pool[Math.floor(Math.random() * pool.length)];
        } while (pool.length > 1 && self.current && target.handle === self.current.handle);

        // Fetch the landing product in the visitor's currency while the reel spins
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var detail = fetch(root + 'products/' + target.handle + '.js')
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          });

        var dur = quiet || reduce ? 0 : Math.max(400, parseInt(self.dataset.spin, 10) || 1800);
        var strip = [self.current || pool[Math.floor(Math.random() * pool.length)]];
        for (var i = 0; i < 16; i++) strip.push(pool[Math.floor(Math.random() * pool.length)]);
        strip.push(target);

        var h = self.reel.parentNode.clientHeight || 175;
        self.reel.style.transition = 'none';
        self.reel.style.transform = 'translateY(0)';
        self.reel.innerHTML = strip
          .map(function (p) {
            return self.card(p);
          })
          .join('');
        self.status.innerHTML = '&gt; spinning';
        void self.reel.offsetWidth;
        if (dur) {
          self.reel.style.transition = 'transform ' + dur + 'ms cubic-bezier(0.12, 0.7, 0.18, 1)';
          self.reel.style.transform = 'translateY(-' + (strip.length - 1) * h + 'px)';
        }

        Promise.all([detail, new Promise(function (res) { setTimeout(res, dur + 30); })]).then(function (out) {
          var p = out[0] || target;
          var variants = p.variants || [];
          var single = variants.length === 1 && variants[0].available;
          var price = typeof p.price === 'number' ? M.money(p.price / 100, cfg.currency, true) : '';
          var url = p.url || root + 'products/' + target.handle;
          var add = single
            ? '<button type="button" class="mx-rec__add" data-variant-id="' +
              variants[0].id +
              '" data-upsell-group="slot" aria-label="Add ' +
              M.esc(p.title) +
              ' to cart">+</button>'
            : '<a href="' + M.esc(url) + '" class="mx-rec__add" aria-label="Choose a size for ' + M.esc(p.title) + '">+</a>';

          self.current = target;
          self.reel.style.transition = 'none';
          self.reel.style.transform = 'translateY(0)';
          self.reel.innerHTML = self.card(
            { title: p.title, images: p.images && p.images.length ? [p.images[0]] : target.images },
            { add: add, price: price, url: url }
          );
          self.classList.remove('is-win');
          void self.offsetWidth;
          self.classList.add('is-win');
          self.status.innerHTML = '&gt; match found';
          if (!quiet) {
            self.spins++;
            if (self.countEl) self.countEl.textContent = self.spins + (self.spins === 1 ? ' spin' : ' spins');
          }
          M.decode(self.querySelector('.mx-rec__title'), String(p.title || '').toUpperCase(), 40, 6);
          self.busy = false;
          self.btn.disabled = false;
        });
      });
    }
  }

  customElements.define('mx-size-swap', MxSizeSwap);
  customElements.define('mx-empty', MxEmpty);
  customElements.define('mx-slot', MxSlot);

  /* ------------------------------------------------------------------ */
  /* Included line under the sizes (hanger, tote): photo on hover or tap */
  /* ------------------------------------------------------------------ */
  /* Same behaviour as the freebie photos. Delegated from the document, because the item
     list is rebuilt on every cart change; only names inside .mx-incl are handled here. */
  var inclFb = function (e) {
    return e.target && e.target.closest ? e.target.closest('#CartDrawer .mx-incl .mx-fb') : null;
  };
  document.addEventListener('pointerover', function (e) {
    if (e.pointerType !== 'mouse') return;
    var fb = inclFb(e);
    if (fb) M.fbOpen(fb, true);
  });
  document.addEventListener('pointerout', function (e) {
    if (e.pointerType !== 'mouse') return;
    var fb = inclFb(e);
    if (fb && !fb.contains(e.relatedTarget) && !fb.dataset.pinned) M.fbOpen(fb, false);
  });
  document.addEventListener('click', function (e) {
    var fb = inclFb(e);
    if (!fb) return;
    e.preventDefault();
    if (fb.dataset.pinned) {
      M.fbOpen(fb, false);
    } else {
      M.fbOpen(fb, true);
      fb.dataset.pinned = 'true';
    }
  });
  document.addEventListener('pointerdown', function (e) {
    var open = document.querySelector('#CartDrawer .mx-incl .mx-fb.is-open');
    if (open && !open.contains(e.target)) M.fbOpen(open, false);
  });

  /* ------------------------------------------------------------------ */
  /* Remove (10.10.2026): the X plays the decode animation while Shopify */
  /* removes the line; the drawer re-renders once both are done, so the */
  /* shipping bar and subtotal still slide and decode as before.        */
  /* ------------------------------------------------------------------ */
  var REMOVE_POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*';

  M.removeCfg = function () {
    var cfg = M.cfg() || {};
    return cfg.remove || {};
  };

  // Every letter and number scrambles, then drops out one by one (decode in reverse)
  M.scrambleOut = function (li, ms) {
    var info = li.querySelector('.mx-item__info');
    var nodes = [];
    if (info) {
      var walker = document.createTreeWalker(info, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          var p = n.parentElement;
          if (!n.nodeValue.trim() || !p || p.closest('.visually-hidden, svg, script')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      var n;
      while ((n = walker.nextNode())) {
        nodes.push({
          n: n,
          t: n.nodeValue,
          d: n.nodeValue.split('').map(function () {
            return Math.random();
          }),
        });
      }
    }
    var t0 = performance.now();
    return new Promise(function (resolve) {
      (function step(now) {
        var p = Math.min(1, (now - t0) / ms);
        nodes.forEach(function (o) {
          o.n.nodeValue = o.t
            .split('')
            .map(function (ch, i) {
              if (ch === ' ') return ' ';
              if (p >= 0.35 + o.d[i] * 0.6) return ' ';
              if (p > o.d[i] * 0.3) return REMOVE_POOL[Math.floor(Math.random() * REMOVE_POOL.length)];
              return ch;
            })
            .join('');
        });
        if (p < 1) requestAnimationFrame(step);
        else resolve();
      })(t0);
    });
  };

  // The row folds shut after the animation
  M.foldRow = function (li, ms) {
    return new Promise(function (resolve) {
      li.style.setProperty('--rm-h', li.offsetHeight + 'px');
      li.style.setProperty('--rm-fold', ms + 'ms');
      li.classList.add('is-folding');
      void li.offsetHeight;
      li.classList.add('is-gone');
      setTimeout(resolve, ms + 30);
    });
  };

  M.parseProps = function (raw) {
    var props = {};
    try {
      var v = JSON.parse(raw || '{}');
      if (Array.isArray(v)) {
        v.forEach(function (pair) {
          if (Array.isArray(pair) && pair.length === 2) props[pair[0]] = pair[1];
        });
      } else if (v && typeof v === 'object') {
        props = v;
      }
    } catch (e) {}
    return props;
  };

  // Puts the freshly rendered drawer and cart icon in place (same as CartItems.onCartUpdate)
  M.applySections = function (sections) {
    if (sections['cart-drawer']) {
      var html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var source = html.querySelector('#CartDrawer .drawer__inner');
      var target = document.querySelector('#CartDrawer .drawer__inner');
      if (source && target) target.innerHTML = source.innerHTML;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && source) drawer.classList.toggle('is-empty', !!source.querySelector('.drawer__inner-empty'));
    }
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      var bubbleHtml = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (bubble && bubbleHtml) bubble.innerHTML = bubbleHtml.innerHTML;
    }
  };

  M.removeLine = function (btn) {
    var li = btn.closest('.mx-item');
    var wrap = btn.closest('cart-remove-button');
    if (!li || !wrap || li.classList.contains('is-removing')) return;
    var line = parseInt(wrap.dataset.index, 10);
    var rc = M.removeCfg();
    var ms = rc.on === false || M.reduced() ? 0 : rc.ms || 900;
    var fold = ms ? rc.fold || 350 : 0;
    var list = li.parentNode;
    var undo = rc.undo
      ? {
          id: Number(btn.dataset.variantId),
          qty: parseInt(btn.dataset.quantity, 10) || 1,
          props: M.parseProps(btn.dataset.properties),
          title: btn.dataset.title || '',
          index: Array.prototype.indexOf.call(list.children, li),
          until: Date.now() + (rc.undoS || 6) * 1000 + ms + fold,
        }
      : null;

    li.classList.add('is-removing');
    btn.setAttribute('aria-busy', 'true');

    var anim = ms
      ? M.scrambleOut(li, ms).then(function () {
          return M.foldRow(li, fold);
        })
      : Promise.resolve();
    var request = fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: 0,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    }).then(function (res) {
      return res.json();
    });

    Promise.all([anim, request])
      .then(function (out) {
        var state = out[1];
        if (!state || state.errors || !state.sections) throw new Error('remove failed');
        if (undo && state.item_count > 0) {
          M._undoSeq = (M._undoSeq || 0) + 1;
          undo.seq = M._undoSeq;
          M._undos.push(undo);
        }
        M.applySections(state.sections);
        M.showUndo();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        }
      })
      .catch(function () {
        window.location.reload();
      });
  };

  // Undo lines (12.10.2026): every removed product keeps its own line in its old place, on its
  // own timer. Called after a remove and from MxShipBar.connectedCallback, so every re-render
  // (quantity change, size swap, another undo) puts the open lines back.
  M._undos = [];
  M.UNDO_FADE_IN = 400;
  M.UNDO_FADE_OUT = 400;

  M.showUndo = function () {
    var list = document.querySelector('#CartDrawer .mx-items');
    var now = Date.now();
    M._undos = M._undos.filter(function (u) {
      if (u.until > now) return true;
      clearTimeout(u.timer);
      return false;
    });
    if (!list) return;
    list.querySelectorAll(':scope > .mx-undo:not(.is-busy):not(.is-fading)').forEach(function (r) {
      r.remove();
    });
    var words = (M.cfg() || {}).remove || {};
    M._undos
      .slice()
      .sort(function (a, b) {
        return a.index - b.index;
      })
      .forEach(function (u) {
        var row = document.createElement('li');
        row.className = 'mx-undo';
        row.dataset.undo = u.seq;
        row.setAttribute('role', 'status');
        row.innerHTML =
          '<span class="mx-undo__txt">' +
          M.esc((words.undoText || '> [product] removed').replace(/\[product\]/g, u.title)) +
          '</span><button type="button" class="mx-undo__btn">' +
          M.esc(words.undoLabel || 'Undo') +
          '</button>';
        // Fade in once: a re-render during the fade-in continues it instead of restarting it
        if (!u.shownAt) u.shownAt = now;
        var since = now - u.shownAt;
        if (since < M.UNDO_FADE_IN) {
          row.classList.add('is-new-row');
          row.style.animationDelay = -since + 'ms';
        }
        list.insertBefore(row, list.children[Math.min(u.index, list.children.length)] || null);
        row.querySelector('.mx-undo__btn').addEventListener('click', function () {
          M.undoLine(u, row);
        });
        clearTimeout(u.timer);
        u.timer = setTimeout(function () {
          M.undoFade(u);
        }, u.until - now);
      });
  };

  // Takes one line out of the list; the lines below it move up one place
  M.dropUndo = function (u) {
    clearTimeout(u.timer);
    M._undos = M._undos.filter(function (x) {
      return x !== u;
    });
    M._undos.forEach(function (x) {
      if (x.index > u.index) x.index--;
    });
  };

  // Fades one line out on its own timer; the other lines stay
  M.undoFade = function (u) {
    M.dropUndo(u);
    var row = document.querySelector('#CartDrawer .mx-undo[data-undo="' + u.seq + '"]');
    if (!row) return;
    row.classList.remove('is-new-row');
    row.classList.add('is-fading');
    setTimeout(function () {
      M.closeRow(row, function () {
        row.remove();
      });
    }, M.UNDO_FADE_OUT);
  };

  // Closes a faded row: its height goes to 0, so the products below slide up (12.10.2026)
  M.UNDO_CLOSE = 350;
  M.closeRow = function (row, done) {
    if (!row.isConnected || M.reduced()) {
      if (done) done();
      return;
    }
    row.style.height = row.offsetHeight + 'px';
    row.classList.add('is-closing');
    void row.offsetHeight;
    row.classList.add('is-closed');
    setTimeout(function () {
      if (done) done();
    }, M.UNDO_CLOSE + 30);
  };

  // Puts that one product back (Shopify adds it at the top); the other lines and buttons stay
  M.undoLine = function (u, row) {
    if (row.classList.contains('is-busy')) return;
    M.dropUndo(u);
    // The product returns at the top, so the lines above its old place move down one
    M._undos.forEach(function (x) {
      if (x.index < u.index) x.index++;
    });
    row.classList.remove('is-new-row');
    row.classList.add('is-busy');
    var item = { id: u.id, quantity: u.qty };
    if (Object.keys(u.props).length) item.properties = u.props;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // The undo line fades and closes first, then the product comes back (at the top) and fades in
    M.arriveNext(u.id);
    var wait = M.reduced() ? 0 : M.UNDO_FADE_OUT;
    new Promise(function (res) {
      setTimeout(function () {
        M.closeRow(row, res);
      }, wait);
    }).then(function () {
      return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
    })
      .then(function () {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mx-undo', cartData: {} });
        }
      })
      .catch(function () {
        window.location.reload();
      });
  };

  /* ---------- Products coming in, tiles moving (12.10.2026) ----------
     A product added back with Undo or from "You might also like" opens its space (350ms) and
     fades in (400ms). Recommendation tiles that move up a place slide there; a product that
     takes over a free place fades in. Nothing appears or disappears at once. */
  M.ARRIVE_OPEN = 350;
  M.ARRIVE_FADE = 400;
  M.arriveNext = function (variantId) {
    M._arrive = { id: String(variantId), until: Date.now() + 10000 };
  };

  M.arriveIn = function (el) {
    if (!el || M.reduced()) return;
    var cs = getComputedStyle(el);
    var h = el.offsetHeight;
    var pt = cs.paddingTop;
    var pb = cs.paddingBottom;
    el.classList.add('is-arriving');
    el.style.height = '0px';
    el.style.paddingTop = '0px';
    el.style.paddingBottom = '0px';
    void el.offsetHeight;
    el.classList.add('is-open');
    el.style.height = h + 'px';
    el.style.paddingTop = pt;
    el.style.paddingBottom = pb;
    setTimeout(function () {
      el.classList.add('is-shown');
      setTimeout(function () {
        el.classList.remove('is-arriving', 'is-open', 'is-shown');
        el.style.height = '';
        el.style.paddingTop = '';
        el.style.paddingBottom = '';
      }, M.ARRIVE_FADE + 30);
    }, M.ARRIVE_OPEN);
  };

  // Remembers where the shown tiles are, so they can slide after the drawer redraws
  M.recSnapshot = function () {
    var map = {};
    document.querySelectorAll('#CartDrawer mx-upsell .mx-rec[data-rec]').forEach(function (t) {
      if (t.offsetParent) map[t.dataset.rec] = t.getBoundingClientRect();
    });
    M._recBefore = { rects: map, until: Date.now() + 10000 };
  };

  M.afterRender = function () {
    M.showUndo();
    var now = Date.now();
    if (M._arrive) {
      var a = M._arrive;
      var x = a.until > now && document.querySelector('#CartDrawer .mx-remove--x[data-variant-id="' + a.id + '"]');
      if (x) {
        M._arrive = null;
        M.arriveIn(x.closest('.mx-item'));
      } else if (a.until <= now) {
        M._arrive = null;
      }
    }
    if (M._recBefore) {
      var before = M._recBefore;
      M._recBefore = null;
      if (before.until <= now || M.reduced()) return;
      document.querySelectorAll('#CartDrawer mx-upsell .mx-rec[data-rec]').forEach(function (t) {
        if (!t.offsetParent) return;
        var o = before.rects[t.dataset.rec];
        if (!o) {
          t.classList.add('is-entering');
          return;
        }
        var n = t.getBoundingClientRect();
        var dx = o.left - n.left;
        var dy = o.top - n.top;
        if (!dx && !dy) return;
        t.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        void t.offsetWidth;
        t.classList.add('is-moving');
        t.style.transform = '';
        setTimeout(function () {
          t.classList.remove('is-moving');
        }, M.ARRIVE_OPEN + 50);
      });
    }
  };

  /* ---------- Quantity change: "> writing to cart" (12.10.2026) ----------
     Same 1px line, small status (types out, then decodes) and dimmed price as the size swap.
     Line and status sit in the gap above the quantity row and take no room. The drawer redraws
     when the cart answers; if it never does, the loading clears after 8 seconds. */
  document.addEventListener(
    'change',
    function (e) {
      var input = e.target && e.target.closest ? e.target.closest('#CartDrawer .mx-qty .quantity__input') : null;
      if (!input) return;
      var li = input.closest('.mx-item');
      var row = li && li.querySelector('.mx-item__actions');
      if (!li || !row || li.classList.contains('is-qloading') || li.classList.contains('is-removing')) return;
      if (String(input.value) === String(input.getAttribute('value'))) return;
      if (parseInt(input.value, 10) === 0) return;
      li.classList.add('is-qloading');
      var line = document.createElement('div');
      line.className = 'mx-qline';
      line.setAttribute('aria-hidden', 'true');
      line.innerHTML = '<i></i>';
      var status = document.createElement('span');
      status.className = 'mx-qstatus';
      status.setAttribute('role', 'status');
      row.append(line, status);
      M.typewrite(status, '> writing to cart', function () {
        M.decode(status.querySelector('.mx-type-text'), '> writing to cart', 40, 6, /[\s>]/);
      });
      setTimeout(function () {
        if (!li.isConnected) return;
        li.classList.remove('is-qloading');
        line.remove();
        status.remove();
      }, 8000);
    },
    true
  );

  // The X: runs before Dawn's own remove handler (cart.js), which would re-render at once
  document.addEventListener(
    'click',
    function (e) {
      var x = e.target && e.target.closest ? e.target.closest('#CartDrawer .mx-remove--x') : null;
      if (!x) return;
      e.preventDefault();
      e.stopPropagation();
      M.removeLine(x);
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Open / close: Stack + Decode (10.10.2026). Titles and amounts       */
  /* decode on open and scramble away on close. Phones close with a     */
  /* drag down (shared assets/mxdvs-sheet-drag.js) or a slide left.     */
  /* Timing comes from --mx-open in mxdvs-cart-drawer.css.              */
  /* ------------------------------------------------------------------ */
  var OPEN_POOL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#%&*';

  M.openMs = function (drawer) {
    var box = (drawer || document).querySelector('#CartDrawer');
    var v = box ? parseFloat(getComputedStyle(box).getPropertyValue('--mx-open')) : NaN;
    return v > 0 ? v : 500;
  };

  M.decodeOpen = function (drawer, out) {
    if (!drawer || M.reduced()) return;
    var ms = M.openMs(drawer) * (out ? 0.6 : 1.2);
    var nodes = [];
    drawer
      .querySelectorAll(
        '#CartDrawer .mx-head__title, #CartDrawer .mx-empty__title, #CartDrawer .mx-item__title, #CartDrawer .mx-item__price, #CartDrawer .mx-foot__val'
      )
      .forEach(function (el) {
        var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        var n;
        while ((n = walker.nextNode())) {
          if (!n.nodeValue.trim()) continue;
          // The real text is kept on the node, so a close right after an open never stores scrambled text
          if (n._mxOrig == null) n._mxOrig = n.nodeValue;
          nodes.push({
            n: n,
            t: n._mxOrig,
            d: n._mxOrig.split('').map(function () {
              return Math.random();
            }),
          });
        }
      });
    if (!nodes.length) return;
    var run = (M._openRun = (M._openRun || 0) + 1);
    var t0 = performance.now();
    (function step(now) {
      if (run !== M._openRun) return;
      var p = Math.min(1, (now - t0) / ms);
      nodes.forEach(function (o) {
        if (p >= 1) {
          o.n.nodeValue = o.t;
          return;
        }
        o.n.nodeValue = o.t
          .split('')
          .map(function (ch, i) {
            // Letters and digits scramble; the currency sign, dots, commas and spaces stay put
            if (!/[A-Za-z0-9]/.test(ch)) return ch;
            var scrambled = out ? p > o.d[i] * 0.5 : p < 0.3 + o.d[i] * 0.7;
            if (!scrambled) return ch;
            return /[0-9]/.test(ch)
              ? String(Math.floor(Math.random() * 10))
              : OPEN_POOL[Math.floor(Math.random() * OPEN_POOL.length)];
          })
          .join('');
      });
      if (p < 1) requestAnimationFrame(step);
    })(t0);
  };

  // assets/mxdvs-sheet-drag.js sits next to this file; it only loads on phones, when the cart opens
  M._selfSrc = (document.currentScript && document.currentScript.src) || '';
  M.loadSheet = function () {
    if (window.MxSheetDrag) return Promise.resolve(true);
    if (M._sheetLoad) return M._sheetLoad;
    var src = M._selfSrc.replace(/cart-drawer\.js(\?.*)?$/, 'mxdvs-sheet-drag.js');
    if (!src || src === M._selfSrc) return Promise.resolve(false);
    M._sheetLoad = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        resolve(!!window.MxSheetDrag);
      };
      s.onerror = function () {
        M._sheetLoad = null;
        resolve(false);
      };
      document.head.appendChild(s);
    });
    return M._sheetLoad;
  };

  /* Slide left to close (phones). The first 6px decide: mostly left = this, mostly down =
     the shared drag down. Sideways rows (sizes, freebie chips, recommendations) and the note
     keep their own swipe. Closes past 25% of the width or with a quick flick. */
  M.swipeLeft = function (panel, close) {
    var x0 = 0,
      y0 = 0,
      dx = 0,
      lastX = 0,
      lastT = 0,
      v = 0,
      on = false,
      armed = false;
    panel.addEventListener(
      'touchstart',
      function (e) {
        var t = e.target;
        armed =
          window.innerWidth < 990 &&
          !(t && t.closest && t.closest('.mx-size__row, .mx-fchips, .mx-recs__grid, .mx-pick, textarea, .mx-term'));
        x0 = lastX = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        lastT = performance.now();
        dx = 0;
        v = 0;
        on = false;
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchmove',
      function (e) {
        if (!armed) return;
        var x = e.touches[0].clientX;
        var ddx = x - x0;
        var ddy = e.touches[0].clientY - y0;
        if (!on) {
          if (ddx < -6 && Math.abs(ddx) > Math.abs(ddy) * 1.5) {
            on = true;
            panel.classList.add('is-drag');
          } else if (Math.abs(ddx) > 6 || Math.abs(ddy) > 6) {
            armed = false;
            return;
          } else return;
        }
        // The drag down stays out of a left slide
        e.stopImmediatePropagation();
        if (e.cancelable) e.preventDefault();
        dx = Math.min(0, ddx);
        var now = performance.now();
        v = (x - lastX) / Math.max(1, now - lastT);
        lastX = x;
        lastT = now;
        panel.style.transform = 'translateX(' + dx + 'px)';
      },
      { passive: false }
    );
    var end = function () {
      if (!on) {
        armed = false;
        return;
      }
      on = armed = false;
      panel.classList.remove('is-drag');
      var w = panel.offsetWidth || 1;
      if (-dx > w * 0.25 || v < -0.6) {
        panel.style.transition = M.reduced() ? 'none' : 'transform 252ms cubic-bezier(.2,.8,.2,1)';
        panel.style.transform = 'translateX(' + -w + 'px)';
        setTimeout(
          function () {
            close();
            // Put the position back once the closed panel is hidden
            setTimeout(function () {
              panel.style.transition = 'none';
              panel.style.transform = '';
              void panel.offsetWidth;
              panel.style.transition = '';
            }, 400);
          },
          M.reduced() ? 0 : 280
        );
      } else {
        panel.style.transition = 'transform 280ms cubic-bezier(.2,.8,.2,1)';
        panel.style.transform = '';
        setTimeout(function () {
          panel.style.transition = '';
        }, 280);
      }
    };
    panel.addEventListener('touchend', end, { passive: true });
    panel.addEventListener('touchcancel', end, { passive: true });
  };

  /* Phones (below 990px): videos behind the open cart pause, and play again when it closes (11.10.2026).
     Same as the menu drawer (assets/mxdvs-menu-skull.js), so the blur behind the cart is not redrawn for
     every video frame. Only the videos paused here play again; videos that were already paused stay paused. */
  M.pauseVideos = function (open) {
    var vids = document.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (open) {
        if (window.innerWidth < 990 && !v.paused) {
          v.dataset.mxcPaused = '1';
          v.pause();
        }
      } else if (v.dataset.mxcPaused) {
        delete v.dataset.mxcPaused;
        var pr = v.play();
        if (pr && pr.catch) pr.catch(function () {});
      }
    }
  };

  M.sheet = function (drawer) {
    if (window.innerWidth >= 990) return;
    var panel = drawer.querySelector('#CartDrawer .mx-drawer__inner');
    if (!panel || panel.dataset.sheet) return;
    panel.dataset.sheet = 'true';
    var close = function () {
      drawer.close();
    };
    // Order matters: slide left first, then the guard, then the shared drag down
    M.swipeLeft(panel, close);
    // A pull that starts in a list scrolled down, or in the note, scrolls instead of dragging
    panel.addEventListener(
      'touchstart',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var list = t.closest('.mx-body, mx-empty');
        if ((list && list.scrollTop > 0) || t.closest('textarea, .mx-term')) e.stopImmediatePropagation();
      },
      { passive: true }
    );
    M.loadSheet().then(function (ok) {
      if (ok && panel.isConnected) MxSheetDrag.attach(panel, { close: close, closePct: 25, flick: 0.6, snapMs: 280 });
    });
  };

  /* Checkout: make sure the freebies attribute is saved before leaving for Shopify checkout */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.id !== 'CartDrawer-Form') return;
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset.mxReady === 'true') return;

      event.preventDefault();
      var button = document.getElementById('CartDrawer-Checkout');
      if (button) button.setAttribute('aria-busy', 'true');

      (M._sync || Promise.resolve())
        .catch(function () {})
        .then(function () {
          form.dataset.mxReady = 'true';
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '';
          form.appendChild(hidden);
          form.submit();
        });
    },
    true
  );
})();
